
import { Separator } from '@repo/ui/components/separator';
import { Link } from 'react-router';
import { Badge } from "@repo/ui/components/badge";
import { IProduct } from '~/types/address';
import { formatVNPriceTyOrTramTrieu, formatPricePerM2, toTrieu, formatTimeUtc7, hostName } from "~/utils/format";
import { PRODUCT_META_ICON } from "~/constants/product";
import { Icon } from './ui/icon';
import { Button } from '@repo/ui/components/button';
import { ArrowRight, MapPin } from '@repo/ui/components/icon';
import { Post } from '~/types/types';
import { Img } from "openimg/react";


function Card({ post }: { post: Post }) {
  return (
    <Link
      to={`/tin-tuc/${post.slug}`}
      className="relative block w-full h-full"
      role="group"
    >
      <div className="w-full h-full cursor-pointer">
        <div className="relative w-full h-full overflow-hidden group rounded-2xl">
          <Img
            width={540}
            height={320}
            fit="cover"
            src={hostName(post.thumbnail[0]?.path || "")}
            className="absolute top-0 left-0 z-0 object-cover w-full h-full duration-300 group-hover:scale-110"
            alt={post.thumbnail[0]?.name || ""}
          />
          <div className="absolute bottom-0 w-full rounded-md h-2/4 group-hover:cursor-pointer bg-grad-strong"></div>
        </div>
        <div className="absolute bottom-0 left-0 px-4 py-2 text-white">
          <span>{formatTimeUtc7(post.publishedAt)}</span>
          <h3 className="text-base md:text-2xl md:leading-8 font-helve">
            {post.title}
          </h3>
        </div>
      </div>
    </Link>
  );
}
function MiniCard({ post }: { post?: Post }) {
  return (
    <Link
      to={`/tin-tuc/${post?.slug}`}
      prefetch="intent"
      className="relative block w-full h-full cursor-pointer"
      role="group"
    >
      <div className="h-full cursor-pointer">
        <div className="relative w-full h-full overflow-hidden group rounded-2xl">
          <img
            width={300}
            height={220}
            src={hostName(post?.thumbnail[0]?.path || "/")}
            // fit="cover"
            alt={post?.thumbnail[0]?.name || ""}
            className="absolute top-0 left-0 z-0 object-cover w-full h-full duration-300 group-hover:scale-110"
          />

          <div className="absolute bottom-0 w-full rounded-md h-2/4 group-hover:cursor-pointer bg-grad-primary "></div>
        </div>
        <div className="absolute bottom-0 left-0 px-4 py-2 text-white ">
          <span>{formatTimeUtc7(post?.publishedAt)} </span>
          <h3 className="font-sans text-sm font-bold leading-6 tracking-0">
            {post?.title}
          </h3>
        </div>
      </div>
    </Link>
  );
}
type CardThumbnailProps = {
  title: string;
  description?: string | null;
  imageUrls?: string[] | null;
  href: string;
};

function CardThumbnail({ title, description, imageUrls, href }: CardThumbnailProps) {
  const coverImage = imageUrls?.[0] || "/image/landing_page/image-2.png";

  return (
    <Link to={href} className="relative block w-full h-full">
      <div className="h-full">
        <div className="relative w-full h-full overflow-hidden rounded-2xl ">
          <img
            src={coverImage}
            className="absolute top-0 left-0 z-0 object-cover w-full h-full duration-300 hover:scale-110"
          />
          <div className="absolute bottom-0 w-full rounded-md h-2/3 group-hover:cursor-pointer bg-grad-mix"></div>
        </div>
        <div className="absolute bottom-0 left-0 px-4 py-5 text-white">

          <h3 className="text-xl md:text-2xl font-helve">{title}</h3>

          <Separator className="my-4 bg-primary-300" />
          <p className="text-sm line-clamp-2">{description}</p>
        </div>
      </div>
    </Link>
  );
}

function CardRealEstate({
  product,
  href,
}: {
  product: IProduct;
  href?: string;
}) {

  const thumb = product?.thumbnails?.[0]?.fileUrl || "/image/landing_page/image-2.png";
  const price = product?.keys?.price ?? 0;
  const area = product?.keys?.area ?? 0;
  const pricePerM2 = area > 0 ? toTrieu(price / area) : 0;
  return (
    <Link
      to={href || (product?.slug ? `/mua-ban-mua-ban-can-ho/${product.slug}` : "/")}
      className="relative w-full h-full"
    >
      <div className="h-full">
        <div className="relative  w-full rounded-2xl overflow-hidden h-[260px]">
          <img
            src={thumb}
            className="absolute top-0 left-0 z-0 object-cover w-full h-full duration-300 hover:scale-110"
          />
        </div>
        <div className="mt-3 space-y-3">
          <Badge variant={"secondary"}>{product?.categoryName}</Badge>
          <h3 className="text-2xl font-helve text-primary min-h-[64px] line-clamp-2">
            {product?.name || "Dự án BID Residence"}
          </h3>

          {/* metas */}
          <div className="flex flex-wrap items-center text-sm gap-x-4 gap-y-2">
            {[
              {
                key: "bedrooms",
                value: product?.keys?.properties?.bedrooms,
                suffix: "",
                icon: PRODUCT_META_ICON.bedrooms,
              },
              {
                key: "wc",
                value: product?.keys?.properties?.wc,
                suffix: "",
                icon: PRODUCT_META_ICON.wc,
              },
              {
                key: "area",
                value: area,
                suffix: "m²",
                icon: PRODUCT_META_ICON.area,
              },
              {
                key: "floor",
                value: product?.keys?.properties?.floorNumber,
                suffix: " tầng",
                icon: PRODUCT_META_ICON.floor,
              },
            ]
              .filter((m) => m.value !== undefined && m.value !== null)
              .map((m) => (
                <div className="flex items-center gap-1" key={String(m.key)}>
                  <Icon name={m.icon} />
                  <span
                    aria-hidden
                    className="inline-block"
                    data-icon={m.icon}
                  />
                  <span>
                    {m.value}
                    {m.suffix}
                  </span>
                </div>
              ))}
          </div>

          {product?.description && (
            <p className="line-clamp-2 text-muted-foreground">
              {product?.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mt-5">
          <div className="font-sans text-xl font-bold text-primary">
            {formatVNPriceTyOrTramTrieu(price)}
          </div>
          <div className="font-medium">{formatPricePerM2(pricePerM2)}</div>
          <div className="flex justify-end flex-1">
            <Button variant={"outline"} size={"lg"}>
              Xem chi tiết
              <ArrowRight />
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
}
function CardRealEstateMini({
  product,
  hiddenButton,
}: {
  product: IProduct;
  hiddenButton?:boolean;
}) {
  const thumb =
    product?.thumbnailUrls?.[0] || "/image/landing_page/image-2.png";
  const price = product?.keys?.price ?? 0;
  const area = product?.keys?.area ?? 0;
  const pricePerM2 = area > 0 ? toTrieu(price / area) : 0;
  return (
    <Link
      to={product ? `/mua-ban-mua-ban-can-ho/${product.slug}` : "/"}
      className="relative w-full h-full text-white"
    >
      <div className="h-full">
        <div className="relative w-full rounded-2xl overflow-hidden h-[260px]">
          <Img
            src={thumb}
            width={357}
            height={241}
            fit="cover"
            className="absolute top-0 left-0 z-0 object-cover w-full h-full duration-300 hover:scale-110"
          />
        </div>
        <div className="mt-3 space-y-3">
          <Badge variant={"secondary"}>{product?.categoryName}</Badge>
          <h3 className="text-2xl text-white font-helve min-h-[60px] line-clamp-2">
            {product?.name}
          </h3>
          {product?.address && (
            <div className="flex gap-2 text-white">
              <span className="inline-block mt-1 ">
                <MapPin size={16} />
              </span>
              <div className="line-clamp-1">{product?.address}</div>
            </div>
          )}
          {/* metas */}
          <div className="flex flex-wrap items-center text-sm gap-x-4 gap-y-2">
            {[
              {
                key: "bedrooms",
                value: product?.keys?.properties?.bedrooms,
                suffix: "",
                icon: PRODUCT_META_ICON.bedrooms,
              },
              {
                key: "wc",
                value: product?.keys?.properties?.wc,
                suffix: "",
                icon: PRODUCT_META_ICON.wc,
              },
              {
                key: "area",
                value: area,
                suffix: "m²",
                icon: PRODUCT_META_ICON.area,
              },
              {
                key: "floor",
                value: product?.keys?.properties?.floorNumber,
                suffix: " tầng",
                icon: PRODUCT_META_ICON.floor,
              },
            ]
              .filter((m) => m.value !== undefined && m.value !== null)
              .map((m) => (
                <div
                  className="flex items-center gap-1 text-white fill-white"
                  key={String(m.key)}
                >
                  <Icon name={m.icon} className="text-xl stroke-current" />
                  <span
                    aria-hidden
                    className="inline-block"
                    data-icon={m.icon}
                  />
                  <span>
                    {m.value}
                    {m.suffix}
                  </span>
                </div>
              ))}
          </div>

          {product?.description && (
            <p className="line-clamp-2">{product?.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2 mt-5 text-white">
          <div className="font-sans text-xl font-bold ">
            {formatVNPriceTyOrTramTrieu(price)}
          </div>
          <div className="font-medium">{formatPricePerM2(pricePerM2)}</div>
          {!hiddenButton && (
            <div className="flex justify-end flex-1">
              <Button variant={"outline"} size={"lg"}>
                Xem chi tiết
                <ArrowRight />
              </Button>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
export { Card, MiniCard, CardThumbnail, CardRealEstate, CardRealEstateMini };
